/**
 * Workflow Engine - Automated email triggers on stage changes
 *
 * Maps pipeline stage transitions to email templates.
 * Supports auto-fire with a cancel window, manual triggers, and scheduled sends.
 */

/**
 * Workflow definitions - maps stage transitions to email actions
 * Each workflow defines what happens when an event moves to a specific stage.
 */
export const WORKFLOW_DEFINITIONS = {
  // ─── GENERAL EVENT WORKFLOWS ───
  enquiry: {
    templateId: 'enquiry_receipt',
    trigger: 'immediate',
    delay: 0,
    label: 'Enquiry Acknowledgement',
    description: 'Thank the client for their enquiry and confirm we received it.',
    cancelWindowSeconds: 10,
    appliesTo: ['corporate', 'charity', 'community', 'private_party', 'music_night', 'restaurant_booking'],
  },
  quote_sent: {
    templateId: 'quote_sent',
    trigger: 'immediate',
    delay: 0,
    label: 'Quote Sent',
    description: 'Send the personalised quote to the client.',
    cancelWindowSeconds: 10,
    appliesTo: ['corporate', 'charity', 'community', 'private_party', 'music_night'],
  },
  confirmed: {
    templateId: 'booking_confirmed',
    trigger: 'immediate',
    delay: 0,
    label: 'Booking Confirmation',
    description: 'Confirm the booking and outline deposit requirements.',
    cancelWindowSeconds: 10,
    appliesTo: ['corporate', 'charity', 'community', 'private_party', 'music_night', 'restaurant_booking'],
  },
  deposit_paid: {
    templateId: 'deposit_received',
    trigger: 'immediate',
    delay: 0,
    label: 'Deposit Received',
    description: 'Acknowledge deposit payment and confirm the event is secured.',
    cancelWindowSeconds: 10,
    appliesTo: ['corporate', 'charity', 'community', 'private_party', 'music_night'],
  },
  complete: {
    templateId: 'post_event_followup',
    trigger: 'delayed',
    delay: 24 * 60 * 60 * 1000, // 24 hours after completion
    label: 'Post-Event Follow-up',
    description: 'Thank the client after the event and request feedback.',
    cancelWindowSeconds: 30,
    appliesTo: ['corporate', 'charity', 'community', 'private_party', 'music_night'],
  },

  // ─── WEDDING-SPECIFIC OVERRIDES ───
  wedding_enquiry: {
    templateId: 'wedding_enquiry_receipt',
    trigger: 'immediate',
    delay: 0,
    label: 'Wedding Enquiry',
    description: 'Congratulate the couple and confirm their wedding enquiry.',
    cancelWindowSeconds: 10,
    appliesTo: ['wedding'],
    overrideStage: 'enquiry',
  },
  wedding_quote_sent: {
    templateId: 'wedding_proposal',
    trigger: 'immediate',
    delay: 0,
    label: 'Wedding Proposal Sent',
    description: 'Send the bespoke wedding proposal to the couple.',
    cancelWindowSeconds: 10,
    appliesTo: ['wedding'],
    overrideStage: 'quote_sent',
  },
  wedding_confirmed: {
    templateId: 'wedding_confirmed',
    trigger: 'immediate',
    delay: 0,
    label: 'Wedding Booking Confirmed',
    description: 'Confirm the wedding booking and next steps.',
    cancelWindowSeconds: 10,
    appliesTo: ['wedding'],
    overrideStage: 'confirmed',
  },
  wedding_complete: {
    templateId: 'wedding_followup',
    trigger: 'delayed',
    delay: 48 * 60 * 60 * 1000, // 48 hours
    label: 'Wedding Follow-up',
    description: 'Send congratulations and request feedback after the wedding.',
    cancelWindowSeconds: 30,
    appliesTo: ['wedding'],
    overrideStage: 'complete',
  },
};

/**
 * Scheduled workflow definitions - time-based triggers
 */
export const SCHEDULED_WORKFLOWS = [
  {
    id: 'deposit_reminder',
    templateId: 'deposit_reminder',
    label: 'Deposit Reminder',
    description: 'Send a reminder when a deposit payment is overdue.',
    trigger: 'scheduled',
    condition: (event) => {
      return (
        event.stage === 'confirmed' &&
        (!event.depositPaid || event.depositPaid === 0)
      );
    },
    daysAfterStageChange: 3,
  },
  {
    id: 'final_invoice',
    templateId: 'final_invoice',
    label: 'Final Invoice',
    description: 'Send the final invoice 7 days before the event.',
    trigger: 'scheduled',
    condition: (event) => {
      const eventDate = new Date(event.date);
      const now = new Date();
      const daysUntil = Math.ceil((eventDate - now) / (1000 * 60 * 60 * 24));
      return (
        (event.stage === 'deposit_paid' || event.stage === 'confirmed') &&
        daysUntil <= 7 &&
        daysUntil > 0
      );
    },
    daysBefore: 7,
  },
  {
    id: 'wedding_final_details',
    templateId: 'wedding_final_details',
    label: 'Wedding Final Details',
    description: 'Request final details 14 days before the wedding.',
    trigger: 'scheduled',
    condition: (event) => {
      const eventDate = new Date(event.date);
      const now = new Date();
      const daysUntil = Math.ceil((eventDate - now) / (1000 * 60 * 60 * 24));
      return event.type === 'wedding' && daysUntil <= 14 && daysUntil > 0;
    },
    daysBefore: 14,
  },
  {
    id: 'event_reminder',
    templateId: 'event_reminder',
    label: 'Event Reminder',
    description: 'Send a reminder 3 days before the event.',
    trigger: 'scheduled',
    condition: (event) => {
      const eventDate = new Date(event.date);
      const now = new Date();
      const daysUntil = Math.ceil((eventDate - now) / (1000 * 60 * 60 * 24));
      return (
        (event.stage === 'deposit_paid' || event.stage === 'confirmed') &&
        daysUntil <= 3 &&
        daysUntil > 0
      );
    },
    daysBefore: 3,
  },
];

/**
 * Get the appropriate workflow for a stage change
 * @param {string} newStage - The stage the event is moving to
 * @param {string} eventType - The event type (wedding, corporate, etc.)
 * @returns {Object|null} The workflow definition, or null if none applies
 */
export const getWorkflowForStageChange = (newStage, eventType) => {
  // Check for type-specific override first (e.g., wedding)
  const overrideKey = Object.keys(WORKFLOW_DEFINITIONS).find((key) => {
    const wf = WORKFLOW_DEFINITIONS[key];
    return (
      wf.overrideStage === newStage &&
      wf.appliesTo.includes(eventType)
    );
  });

  if (overrideKey) {
    return { ...WORKFLOW_DEFINITIONS[overrideKey], workflowKey: overrideKey };
  }

  // Fall back to general workflow
  const generalWorkflow = WORKFLOW_DEFINITIONS[newStage];
  if (generalWorkflow && generalWorkflow.appliesTo.includes(eventType)) {
    return { ...generalWorkflow, workflowKey: newStage };
  }

  return null;
};

/**
 * Get all scheduled workflows that should fire for a given set of events
 * @param {Array} events - Array of event objects
 * @param {Array} sentEmails - Array of already-sent email records
 * @returns {Array} Workflows that should fire, with their target events
 */
export const getScheduledWorkflowsDue = (events, sentEmails = []) => {
  const due = [];

  events.forEach((event) => {
    SCHEDULED_WORKFLOWS.forEach((workflow) => {
      // Check condition
      if (!workflow.condition(event)) return;

      // Check if already sent
      const alreadySent = sentEmails.some(
        (email) =>
          email.eventId === event.id &&
          email.templateId === workflow.templateId
      );
      if (alreadySent) return;

      due.push({
        workflow,
        event,
      });
    });
  });

  return due;
};

/**
 * Create a pending email action from a workflow trigger
 * @param {Object} workflow - Workflow definition
 * @param {Object} event - Event object
 * @param {Object} template - Email template
 * @returns {Object} Pending email action
 */
export const createPendingEmailAction = (workflow, event, template) => {
  return {
    id: `pending_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
    workflowKey: workflow.workflowKey || workflow.id,
    templateId: workflow.templateId,
    templateName: template?.name || workflow.label,
    eventId: event.id,
    eventName: event.name,
    clientName: event.client,
    clientEmail: event.clientEmail || event.email || '',
    subject: template?.subject || '',
    body: template?.body || '',
    trigger: workflow.trigger,
    cancelWindowSeconds: workflow.cancelWindowSeconds || 10,
    createdAt: new Date().toISOString(),
    status: 'pending', // pending → sending → sent | cancelled
  };
};
